// hooks/useZeno.js
import { useState, useCallback, useRef, useEffect } from 'react';
import ZenoService from '../services/zeno.service';
import { useAuth } from '../useAuth';
import { useOrganizations } from './useOrganizations';

/**
 * Hook para interactuar con Zeno Bot
 */
export const useZeno = () => {
  const { user } = useAuth();
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;
  const userId = user?.user_id;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [streamingContent, setStreamingContent] = useState(''); // Contenido parcial durante streaming
  const [error, setError] = useState(null);
  const [contentBudget, setContentBudget] = useState(null);
  const [zenoInfo, setZenoInfo] = useState(null);
  const [zenoOnline, setZenoOnline] = useState(null); // null = checking, true/false = reachable?
  const [currentChatHistoryId, setCurrentChatHistoryId] = useState(null);
  const [chatHistories, setChatHistories] = useState([]);
  const [chatHistoriesTotal, setChatHistoriesTotal] = useState(0);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const chatSearchRef = useRef(''); // current title search, so paging keeps the filter
  const chatHistoriesRef = useRef([]); // mirror of the list, for reads inside callbacks
  const loadZenoChatsRef = useRef(); // Ref para evitar dependencias infinitas
  const streamControllerRef = useRef(null); // Ref para controller del stream

  /**
   * Cargar información de Zeno
   */
  const loadZenoInfo = useCallback(async () => {
    try {
      const result = await ZenoService.getZenoInfo();
      if (result.success) {
        setZenoInfo(result.data);
      }
    } catch (err) {
      console.error('Error cargando info de Zeno:', err);
    }
  }, []);

  /**
   * Chequear salud de Zeno — solo alcanzabilidad: si /zeno/health responde,
   * Zeno está Online; si falla (red / 5xx / timeout), Offline. El payload es
   * un stub en el backend, así que no interpretamos su contenido.
   */
  const checkZenoHealth = useCallback(async () => {
    try {
      const result = await ZenoService.getZenoHealth();
      setZenoOnline(!!result?.success);
    } catch (err) {
      setZenoOnline(false);
    }
  }, []);

  const normalizeChatInput = useCallback((input) => {
    if (typeof input === 'string') {
      return {
        message: input,
        contentBlocks: null,
      };
    }

    return {
      message: input?.message || '',
      contentBlocks: Array.isArray(input?.contentBlocks) ? input.contentBlocks : null,
    };
  }, []);

  /**
   * Chatear con Zeno - actualizado para nueva API
   */
  const chatWithZeno = useCallback(async (input, workflowContext = null, chatHistoryId = null) => {
    const { message, contentBlocks } = normalizeChatInput(input);

    if (!accountId || !userId || (!message.trim() && !contentBlocks?.length)) return;

    setSending(true);
    setError(null);

    // Agregar mensaje del usuario inmediatamente
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      message: message,
      contentBlocks: contentBlocks || undefined,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // Mostrar que Zeno está escribiendo casi inmediatamente
      setTimeout(() => setIsTyping(true), 50);
      
      // Crear contexto web por defecto
      const context = {
        platform: "web",
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      };

      const result = await ZenoService.chatWithZeno({
        accountId,
        userId,
        message,
        contentBlocks,
        chatHistoryId: chatHistoryId || currentChatHistoryId, // Usar chatHistoryId actual si no se proporciona uno
        context,
        workflowContext,
        useMemory: true,
      });

      if (result.success) {
        // Guardar el chatHistoryId para continuar la conversación
        if (result.data.chatHistoryId) {
          setCurrentChatHistoryId(result.data.chatHistoryId);
        }

        // Agregar respuesta de Zeno
        const zenoMessage = {
          id: result.data.messageId || `zeno-${Date.now()}`,
          role: 'assistant',
          message: result.data.response,
          workflowSuggestion: result.data.workflowSuggestion || null,
          chatHistoryId: result.data.chatHistoryId,
          nextActions: result.data.nextActions || null,
          metadata: result.data.metadata || null,
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => m.id !== userMessage.id);
          return [...withoutTemp, userMessage, zenoMessage];
        });

        // Refrescar el historial SOLO si es un chat nuevo, y en silencio (sin flash).
        refreshChatsAfterResponse(result.data.chatHistoryId);

        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error comunicándose con Zeno';
      setError(errorMsg);
      console.error(err);
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      return { success: false, error: { message: errorMsg } };
    } finally {
      setSending(false);
      setIsTyping(false);
    }
  }, [accountId, userId, currentChatHistoryId, normalizeChatInput]);

  /**
   * Chatear con Zeno - STREAMING VERSION
   * La respuesta aparece carácter por carácter en tiempo real
   */
  const chatWithZenoStream = useCallback(async (input, workflowContext = null, chatHistoryId = null) => {
    const { message, contentBlocks } = normalizeChatInput(input);

    if (!accountId || !userId || (!message.trim() && !contentBlocks?.length)) return;

    // Cancelar stream anterior si existe
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
    }

    setSending(true);
    setError(null);
    setStreamingContent('');

    // Agregar mensaje del usuario inmediatamente
    const userMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      message: message,
      contentBlocks: contentBlocks || undefined,
      createdAt: new Date().toISOString(),
    };

    // Agregar placeholder para respuesta de Zeno
    const zenoPlaceholder = {
      id: `temp-zeno-${Date.now()}`,
      role: 'assistant',
      message: '',
      isStreaming: true,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage, zenoPlaceholder]);
    setIsTyping(true);

    // Crear contexto web por defecto
    const context = {
      platform: "web",
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    let accumulatedContent = '';
    let metadata = null;

    const { controller } = ZenoService.chatWithZenoStream(
      {
        accountId,
        userId,
        message,
        contentBlocks,
        chatHistoryId: chatHistoryId || currentChatHistoryId,
        context,
        workflowContext,
        useMemory: true,
      },
      {
        onContent: (chunk) => {
          accumulatedContent += chunk;
          setStreamingContent(accumulatedContent);
          
          // Actualizar el mensaje de Zeno en tiempo real
          setMessages((prev) => {
            const updated = [...prev];
            const zenoIndex = updated.findIndex(m => m.id === zenoPlaceholder.id);
            if (zenoIndex !== -1) {
              updated[zenoIndex] = {
                ...updated[zenoIndex],
                message: accumulatedContent,
              };
            }
            return updated;
          });
        },
        onMetadata: (meta) => {
          metadata = meta;
          // Actualizar chatHistoryId si viene en metadata
          if (meta?.chatHistoryId) {
            setCurrentChatHistoryId(meta.chatHistoryId);
          }
        },
        onDone: async (data) => {
          // Finalizar el mensaje de Zeno
          setMessages((prev) => {
            const updated = [...prev];
            const zenoIndex = updated.findIndex(m => m.id === zenoPlaceholder.id);
            if (zenoIndex !== -1) {
              updated[zenoIndex] = {
                ...updated[zenoIndex],
                id: metadata?.messageId || `zeno-${Date.now()}`,
                message: accumulatedContent,
                isStreaming: false,
                workflowSuggestion: data?.workflowSuggestion || metadata?.workflowSuggestion || null,
                chatHistoryId: data?.chatHistoryId || metadata?.chatHistoryId,
                nextActions: data?.nextActions || metadata?.nextActions || null,
                metadata: metadata,
              };
            }
            return updated;
          });

          // Refrescar el historial SOLO si es un chat nuevo, y en silencio (sin flash).
          refreshChatsAfterResponse(data?.chatHistoryId || metadata?.chatHistoryId);

          setSending(false);
          setIsTyping(false);
          setStreamingContent('');
          setContentBudget(null);
          streamControllerRef.current = null;
        },
        onError: (err) => {
          console.error('Error en streaming con Zeno:', err);

          // Check for content budget exceeded error
          if (err.status === 400 && err.data?.detail?.error === 'content_budget_exceeded') {
            const budgetData = err.data.detail.content_budget;
            if (budgetData) {
              setContentBudget(budgetData);
            }
          }

          setError(err.message || 'Error comunicándose con Zeno');
          
          // Remover mensajes temporales
          setMessages((prev) => prev.filter(
            m => m.id !== userMessage.id && m.id !== zenoPlaceholder.id
          ));

          setSending(false);
          setIsTyping(false);
          setStreamingContent('');
          streamControllerRef.current = null;
        },
      }
    );

    streamControllerRef.current = controller;
    return { controller };
  }, [accountId, userId, currentChatHistoryId, normalizeChatInput]);

  /**
   * Cancelar stream en curso
   */
  const cancelStream = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
      setSending(false);
      setIsTyping(false);
      setStreamingContent('');
    }
  }, []);

  /**
   * Obtener sugerencia de workflow
   */
  const suggestWorkflow = useCallback(async (workflowDescription, context = null) => {
    if (!accountId || !workflowDescription.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await ZenoService.suggestWorkflow({
        accountId, // Cambiar de userId a accountId
        workflowDescription,
        context,
        accountId,
      });

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error obteniendo sugerencia de workflow';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  /**
   * Limpiar chat
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    setIsTyping(false);
    setCurrentChatHistoryId(null); // Limpiar también el chatHistoryId
  }, []);

  /**
   * Cargar lista de chats con Zeno
   */
  const CHATS_PAGE_SIZE = 30;

  // Keep a ref in sync so callbacks (e.g. the post-response refresh) can read the
  // current list without being re-created on every change.
  useEffect(() => { chatHistoriesRef.current = chatHistories; }, [chatHistories]);

  // `silent`: skip the loading flag so a background refresh doesn't flash the
  // skeleton over the list. The list still updates in place (stable keys).
  const loadZenoChats = useCallback(async (options = {}) => {
    if (!accountId) return;

    // Reset load (offset 0). If a search term is passed, remember it so the
    // subsequent loadMoreChats pages keep the same filter.
    const search = typeof options.search === 'string' ? options.search : chatSearchRef.current;
    chatSearchRef.current = search;

    if (!options.silent) setLoadingChats(true);
    try {
      const result = await ZenoService.listZenoChats(accountId, {
        limit: CHATS_PAGE_SIZE,
        offset: 0,
        search,
      });
      if (result.success) {
        setChatHistories(result.data || []);
        setChatHistoriesTotal(result.total || 0);
      } else {
        console.error('Error loading Zeno chats:', result.error);
      }
    } catch (error) {
      console.error('Error loading Zeno chats:', error);
    } finally {
      if (!options.silent) setLoadingChats(false);
    }
  }, [accountId]);

  // Called after Zeno replies. Only pull a fresh page when a *new* chat was
  // created (its id isn't in the list yet) — and silently, so nothing flashes.
  // If the chat already exists, just move it to the top locally (no request).
  const refreshChatsAfterResponse = useCallback((chatHistoryId) => {
    if (!chatHistoryId) return;
    const list = chatHistoriesRef.current || [];
    const idx = list.findIndex((c) => c.id === chatHistoryId);
    if (idx === -1) {
      loadZenoChatsRef.current?.({ silent: true });
    } else if (idx > 0) {
      setChatHistories((prev) => {
        const i = prev.findIndex((c) => c.id === chatHistoryId);
        if (i <= 0) return prev;
        const next = [...prev];
        const [item] = next.splice(i, 1);
        next.unshift(item);
        return next;
      });
    }
  }, []);

  // Append the next page of chats (infinite scroll). Dedupes by id and keeps
  // the current search filter.
  const loadMoreChats = useCallback(async () => {
    if (!accountId || loadingMoreChats) return;
    const offset = chatHistories.length;
    if (offset >= chatHistoriesTotal) return; // nothing more to load

    setLoadingMoreChats(true);
    try {
      const result = await ZenoService.listZenoChats(accountId, {
        limit: CHATS_PAGE_SIZE,
        offset,
        search: chatSearchRef.current,
      });
      if (result.success) {
        setChatHistories((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const fresh = (result.data || []).filter((c) => !seen.has(c.id));
          return [...prev, ...fresh];
        });
        setChatHistoriesTotal(result.total || 0);
      }
    } catch (error) {
      console.error('Error loading more Zeno chats:', error);
    } finally {
      setLoadingMoreChats(false);
    }
  }, [accountId, loadingMoreChats, chatHistories.length, chatHistoriesTotal]);

  // Asignar ref para poder llamar desde chatWithZeno
  loadZenoChatsRef.current = loadZenoChats;

  /**
   * Cargar una conversación específica
   */
  const loadConversation = useCallback(async (chatHistoryId) => {
    if (!chatHistoryId) return;

    setLoading(true);
    setError(null);
    
    try {
      const result = await ZenoService.loadChatMessages(chatHistoryId);
      
      if (result.success) {
        setMessages(result.data.messages || []);
        setCurrentChatHistoryId(chatHistoryId);
        return { success: true };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMsg = 'Error loading conversation';
      setError(errorMsg);
      console.error('loadConversation error:', error);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [setMessages, setCurrentChatHistoryId, setLoading, setError]);

  /**
   * Iniciar nueva conversación
   */
  const startNewConversation = useCallback(() => {
    clearChat();
    // Refrescar historial de chats al iniciar nueva conversación
    if (typeof loadZenoChatsRef.current === 'function') {
      loadZenoChatsRef.current();
    }
  }, [clearChat]);

  return {
    messages,
    loading,
    sending,
    isTyping,
    streamingContent, // Contenido parcial durante streaming
    error,
    contentBudget, // Content budget data when file exceeds token limit
    setContentBudget, // Allow consumer to clear budget state
    zenoInfo,
    zenoOnline, // Estado de alcanzabilidad de Zeno (null = checking)
    checkZenoHealth, // Disparar un chequeo de salud
    currentChatHistoryId, // Exponer el chatHistoryId actual
    chatHistories, // Lista de conversaciones anteriores
    chatHistoriesTotal, // Total de chats (para saber si hay más)
    chatsHasMore: chatHistories.length < chatHistoriesTotal, // ¿quedan más por cargar?
    loadingChats, // Estado de carga de chats
    loadingMoreChats, // Cargando la siguiente página (scroll infinito)
    loadMoreChats, // Cargar la siguiente página de chats
    chatWithZeno,
    chatWithZenoStream, // Version con streaming
    cancelStream, // Cancelar stream en curso
    sendMessage: chatWithZeno, // Alias para compatibilidad
    sendMessageStream: chatWithZenoStream, // Alias para compatibilidad
    suggestWorkflow,
    clearChat,
    loadZenoInfo,
    loadZenoChats, // Cargar lista de chats
    loadConversation, // Cargar conversación específica
    startNewConversation, // Iniciar nueva conversación
  };
};
